import {Outlet, useLocation} from "react-router-dom";
import React, {useCallback, useEffect, useRef} from "react";
import {Amplify} from "aws-amplify";
import awsconfig from "./amplifyConfig";
import {useDispatch, useSelector} from "react-redux";
import {
    selectLoginState,
    selectUsername, selectApplicationVersion, selectIsLatestVersion, setIsLatestVersion
} from "./features/states/slices";
import UnityCommandsContext from "./components/shared/UnityCommandsContext";
import {useTrackingCode} from "react-hubspot-tracking-code-hook";
import {checkApplicationVersion, shouldShowAnimotiveUpdatePage} from "./components/shared/AnimotiveVersionActions";
import AnimotiveUpdatePage from "./pages/AnimotiveUpdatePage";
import {UserNameInput} from "./pages/UserNameInput";
import {bootstrapAuthSession, loadLoggedInAppData} from "./features/states/thunks";
import SiteFooter from "./components/shared/SiteFooter";
import { preloadProjectThumbnails } from "./functions/ProjectThumbnailFunctions";
import { isHostedVuplexEnvironment } from "./routes/vuplexRouter";
import "./App.css";

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
Amplify.configure(awsconfig);

function AppShell({ children }) {
    const { pathname } = useLocation();
    const isDarkRoute = pathname === "/download" || pathname === "/checkout";

    return (
        <div className={isDarkRoute ? "app-shell app-shell--dark" : "app-shell"}>
            <main className={"app-shell__main"}>
                {children}
            </main>
            <SiteFooter />
        </div>
    );
}

export const DisplayForUsersNotLoggedIn = () => {
    return <AppShell><Outlet/></AppShell>;
};

export const MainDisplay = () => {
    return <AppShell><Outlet/></AppShell>;
};

export function MainBody({children, fullWidth = false}) {
    return (
        <div className={fullWidth ? "app-main-body app-main-body--full-width" : "app-main-body"}>
            {children ?? <Outlet/>}
        </div>
    );
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            throwOnError: true,
        },
    },
});

function App() {

    const dispatch = useDispatch()
    const userLoggedIn = useSelector(selectLoginState)

    const userNames = useSelector(selectUsername)
    const {setIdentity} = useTrackingCode();

    const [userNameFound, setUserNameFound] = React.useState(true)
    const hasLoadedLoggedInAppDataRef = useRef(false);
    const latestSetIdentityRef = useRef(setIdentity);

    const applicationVersion = useSelector(selectApplicationVersion)
    const isLatestVersion = useSelector(selectIsLatestVersion)
    const showAnimotiveUpdateScreen = shouldShowAnimotiveUpdatePage({
        isHostedVuplex: isHostedVuplexEnvironment(),
        isLatestVersion,
    });

    useEffect(() => {
        dispatch(bootstrapAuthSession());
    }, [dispatch]);

    useEffect(() => {
        preloadProjectThumbnails();
    }, []);

    useEffect(() => {
        latestSetIdentityRef.current = setIdentity;
    }, [setIdentity]);

    const checkIfLatestVersion = useCallback(async () => {
      let response = await checkApplicationVersion();
      dispatch(setIsLatestVersion(response));
    }, [dispatch]);

    useEffect(() => {
        if (!userLoggedIn) {
            hasLoadedLoggedInAppDataRef.current = false;
            return;
        }

        if (hasLoadedLoggedInAppDataRef.current) {
            return;
        }

        hasLoadedLoggedInAppDataRef.current = true;
        dispatch(loadLoggedInAppData({setIdentity: latestSetIdentityRef.current}));
    }, [dispatch, userLoggedIn]);

    useEffect(() => {
      checkIfLatestVersion();
    }, [applicationVersion, checkIfLatestVersion]);

    useEffect(() => {
      if (userNames === undefined) {
        setUserNameFound(false);
      }
    }, [userNames]);

    // localhost:3000
    if(userNameFound === false && userLoggedIn === true){
        return (
            <UnityCommandsContext userloggedIn={userLoggedIn}>
                <AppShell>
                    <UserNameInput/>
                </AppShell>
            </UnityCommandsContext>
        );
    }

    if (userLoggedIn === undefined) {
        return null;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <UnityCommandsContext userloggedIn={userLoggedIn}>
                <div className={"app-root-shell"}>
                    {showAnimotiveUpdateScreen
                        ? <AppShell><AnimotiveUpdatePage /></AppShell>
                        : userLoggedIn
                            ? <MainDisplay />
                            : <DisplayForUsersNotLoggedIn />}
                </div>
            </UnityCommandsContext>
        </QueryClientProvider>
    );
}

export default App;
